import { useEffect } from 'react';

/**
 * Component that disables various browser behaviors for a native app-like experience
 * This handles behaviors that can't be disabled with CSS alone
 */
export const DisableBrowserBehaviors: React.FC = () => {
  useEffect(() => {
    // Prevent double-tap zoom on iOS
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (event: TouchEvent) => {
      const now = new Date().getTime();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Prevent pinch-to-zoom gestures
    const preventPinchZoom = (event: TouchEvent) => {
      if (event.touches.length > 1) {
        event.preventDefault();
      }
    };

    // Prevent context menu on long press (right-click equivalent)
    const preventContextMenu = (event: Event) => {
      event.preventDefault();
      return false;
    };

    // Prevent drag and drop
    const preventDragStart = (event: Event) => {
      event.preventDefault();
      return false;
    };

    // Prevent text selection on touch
    const preventSelectStart = (event: Event) => {
      const target = event.target as HTMLElement;
      // Allow text selection in input fields and elements with allow-select class
      if (
        target.tagName === 'INPUT' || 
        target.tagName === 'TEXTAREA' || 
        target.contentEditable === 'true' ||
        target.closest('.allow-select')
      ) {
        return true;
      }
      event.preventDefault();
      return false;
    };

    // More aggressive prevention of browser back/forward swipe gestures
    const preventSwipeNavigation = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-dnd-region]')) return;

      // Allow touches on bottom navigation and important UI elements
      if (target.closest('nav, .bottom-nav, button, input, [role="button"]')) {
        return; // Don't block touches on interactive elements
      }

      // Only allow swipe gestures on elements with custom-swipe class
      if (!target.closest('.custom-swipe')) {
        if (event.touches.length === 1) {
          const touch = event.touches[0];
          const startX = touch.clientX;
          const startY = touch.clientY;
          
                  // Don't block touches in the bottom safe area (bottom navigation + home indicator)
        const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0') || 0;
        const bottomThreshold = 80 + safeAreaBottom; // 80px for nav + safe area
        if (startY > window.innerHeight - bottomThreshold) {
          return;
        }
          
          // Much smaller edge detection to prevent Safari swipe navigation
          if (startX < 20 || startX > window.innerWidth - 20) {
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();
            return false;
          }
        }
        
        // Also prevent any multi-touch gestures that aren't on custom-swipe elements
        if (event.touches.length > 1) {
          event.preventDefault();
          event.stopPropagation();
          return false;
        }
      }
    };

    // Additional touchmove handler specifically for edge navigation
    const preventEdgeSwipeMove = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      if (target.closest('[data-dnd-region]')) return;

      // Allow touches on interactive elements
      if (target.closest('nav, .bottom-nav, button, input, [role="button"]')) {
        return;
      }

      if (!target.closest('.custom-swipe') && event.touches.length === 1) {
        const touch = event.touches[0];
        const currentX = touch.clientX;
        const currentY = touch.clientY;
        
        // Don't block touches in the bottom navigation area
        // Don't block touches in the bottom safe area (bottom navigation + home indicator)
        const safeAreaBottom = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--sab') || '0') || 0;
        const bottomThreshold = 80 + safeAreaBottom; // 80px for nav + safe area
        if (currentY > window.innerHeight - bottomThreshold) {
          return;
        }
        
        // MUCH smaller edge detection - only 20px from extreme edges
        if (currentX < 20 || currentX > window.innerWidth - 20) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          return false;
        }
      }
    };

    // Prevent scroll chaining (overscroll behavior)
    const preventScrollChaining = (event: TouchEvent) => {
      const target = event.target as HTMLElement;
      // Don't block touchmove inside DnD regions (e.g. floor plan drag) — prevents drag from following finger on tablet
      if (target.closest('[data-dnd-region]')) return;
      const scrollableParent = target.closest('.overflow-auto, .overflow-y-auto, .overflow-x-auto');
      
      if (!scrollableParent) {
        // If not in a scrollable container, prevent default to avoid browser behaviors
        if (event.touches.length === 1) {
          event.preventDefault();
        }
      }
    };

    // Add event listeners
    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });
    document.addEventListener('touchstart', preventPinchZoom, { passive: false });
    document.addEventListener('touchmove', preventPinchZoom, { passive: false });
    document.addEventListener('contextmenu', preventContextMenu, { passive: false });
    document.addEventListener('dragstart', preventDragStart, { passive: false });
    document.addEventListener('selectstart', preventSelectStart, { passive: false });
    document.addEventListener('touchstart', preventSwipeNavigation, { passive: false });
    document.addEventListener('touchmove', preventEdgeSwipeMove, { passive: false });
    document.addEventListener('touchmove', preventScrollChaining, { passive: false });

    // Disable text selection globally via CSS (backup)
    document.body.style.webkitUserSelect = 'none';
    document.body.style.userSelect = 'none';

    // Cleanup function
    return () => {
      document.removeEventListener('touchend', preventDoubleTapZoom);
      document.removeEventListener('touchstart', preventPinchZoom);
      document.removeEventListener('touchmove', preventPinchZoom);
      document.removeEventListener('contextmenu', preventContextMenu);
      document.removeEventListener('dragstart', preventDragStart);
      document.removeEventListener('selectstart', preventSelectStart);
      document.removeEventListener('touchstart', preventSwipeNavigation);
      document.removeEventListener('touchmove', preventEdgeSwipeMove);
      document.removeEventListener('touchmove', preventScrollChaining);
      
      // Restore defaults
      document.body.style.webkitUserSelect = '';
      document.body.style.userSelect = '';
    };
  }, []);

  // This component doesn't render anything
  return null;
};
